/**
 * calendarCoverage.ts — the "dates with nothing planned" check. Plain date arithmetic on the calendar entries and the
 * content already dated; it is not AI and it never blocks anything.
 */
import { z } from "zod";
import { addDays, calendarEventKindSchema, daysBetween, type EventOccurrence } from "./calendarEvents.ts";

// --- "dates with nothing planned" ---

export const COVERAGE_HORIZON_DAYS = 45;
/** Content counts for a date when it lands from this many days before the date up to the last day of it. */
export const COVERAGE_LEAD_DAYS = 10;

export const coverageGapSchema = z.object({
  id: z.string(),
  title: z.string(),
  kind: calendarEventKindSchema,
  startDate: z.string(),
  endDate: z.string(),
  daysAway: z.number().int(),
  /** How many dated pieces fall in the lead-up window. 0 means a gap. */
  covered: z.number().int(),
});
export type CoverageGap = z.infer<typeof coverageGapSchema>;
export const coverageResponseSchema = z.object({ today: z.string(), horizonDays: z.number().int(), items: z.array(coverageGapSchema) });

/**
 * Upcoming entries (not plain notes) and how many pieces of content are dated in their lead-up window.
 * `contentDays` are site-calendar dates of everything scheduled, planned or published. Returns every upcoming entry, covered or not, nearest first.
 */
export function coverageFor(occurrences: readonly EventOccurrence[], contentDays: readonly string[], today: string, horizonDays = COVERAGE_HORIZON_DAYS): CoverageGap[] {
  const last = addDays(today, horizonDays);
  const out: CoverageGap[] = [];
  for (const o of occurrences) {
    if (o.kind === "note") continue;
    if (o.endDate < today || o.startDate > last) continue;
    const from = addDays(o.startDate, -COVERAGE_LEAD_DAYS);
    const covered = contentDays.filter((d) => d >= from && d <= o.endDate).length;
    out.push({ id: o.id, title: o.title, kind: o.kind, startDate: o.startDate, endDate: o.endDate, daysAway: Math.max(0, daysBetween(today, o.startDate)), covered });
  }
  return out.sort((a, b) => a.startDate.localeCompare(b.startDate) || a.title.localeCompare(b.title));
}
